import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { isDemoDataSource } from '@/lib/data-source.ts'
import { queryKeys } from '@/lib/query/queryKeys.ts'
import { getSupabaseClient } from '@/lib/supabase/client.ts'
import type { MyBookingRow } from '@/lib/supabase/database.types.ts'
import { useTenant } from '@/tenant/useTenant.ts'
import { unsubscribeClientDevice } from '@/lib/domain/clientPush.ts'

/**
 * The client's side of Supabase Auth: sign in by an emailed link, no password.
 *
 * The same auth as the owner's cabinet, on purpose — one session store, one
 * set of policies. What makes someone an owner is a `tenant_members` row, not
 * the way they signed in; a client account has none and reaches its bookings
 * only through `my_bookings`, `claim_booking` and `cancel_my_booking`.
 */
export interface ClientSession {
  readonly userId: string
  readonly email: string
}

export type ClientSessionStatus = 'unavailable' | 'pending' | 'signed-out' | 'signed-in'

async function loadClientSession(): Promise<ClientSession | null> {
  const { data } = await getSupabaseClient().auth.getSession()
  const user = data.session?.user
  return user === undefined ? null : { userId: user.id, email: user.email ?? '' }
}

export function useClientSession(): {
  readonly session: ClientSession | null
  readonly status: ClientSessionStatus
} {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: queryKeys.clientSession,
    queryFn: loadClientSession,
    enabled: !isDemoDataSource,
    staleTime: Infinity,
  })

  // The emailed link lands with the session in the URL; supabase-js picks it
  // up and fires this. Sign-out and token refresh arrive the same way.
  useEffect(() => {
    if (isDemoDataSource) return
    const { data } = getSupabaseClient().auth.onAuthStateChange(() => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clientSession })
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('my-bookings'),
      })
    })
    return () => {
      data.subscription.unsubscribe()
    }
  }, [queryClient])

  if (isDemoDataSource) return { session: null, status: 'unavailable' }
  if (query.status !== 'success') return { session: null, status: 'pending' }
  return { session: query.data, status: query.data === null ? 'signed-out' : 'signed-in' }
}

/** Sends the sign-in link. The link returns to `redirectTo`, already signed in. */
export function useSendSignInLink() {
  return useMutation({
    mutationFn: async (input: { email: string; redirectTo: string }) => {
      const { error } = await getSupabaseClient().auth.signInWithOtp({
        email: input.email.trim(),
        options: { emailRedirectTo: input.redirectTo, shouldCreateUser: true },
      })
      if (error !== null) throw new Error(error.message)
    },
  })
}

export function useVerifySignInCode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { email: string; token: string }) => {
      const { data, error } = await getSupabaseClient().auth.verifyOtp({
        email: input.email.trim(), token: input.token.trim(), type: 'email',
      })
      if (error !== null) throw new Error(error.message)
      if (data.session === null || data.user === null) throw new Error('No session returned')
      return { userId: data.user.id, email: data.user.email ?? '' }
    },
    onSuccess: (session) => {
      queryClient.setQueryData(queryKeys.clientSession, session)
      void queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes('my-bookings') })
    },
  })
}

export function useClientSignOut() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await unsubscribeClientDevice(null)
      // Local scope: signing out here must not end the same person's session
      // on their other devices.
      const { error } = await getSupabaseClient().auth.signOut({ scope: 'local' })
      if (error !== null) throw new Error(error.message)
    },
    onSuccess: () => {
      queryClient.removeQueries({ predicate: query => query.queryKey.includes('client-contacts') || query.queryKey.includes('client-device') || query.queryKey.includes('my-bookings') })
      queryClient.setQueryData(queryKeys.clientSession, null)
    },
  })
}

export function useMyBookings(enabled: boolean) {
  const tenant = useTenant()
  return useQuery({
    queryKey: queryKeys.myBookings(tenant.slug),
    enabled: enabled && !isDemoDataSource,
    queryFn: async (): Promise<readonly MyBookingRow[]> => {
      const { data, error } = await getSupabaseClient().rpc('my_bookings', { p_slug: tenant.slug })
      if (error !== null) throw new Error(error.message)
      return (data ?? []) as MyBookingRow[]
    },
  })
}

export function useCancelMyBooking() {
  const tenant = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (bookingId: string) => {
      const { error } = await getSupabaseClient().rpc('cancel_my_booking', { p_booking_id: bookingId })
      if (error !== null) {
        throw new Error(
          error.code === '23514'
            ? 'Эту запись уже нельзя отменить онлайн — позвоните в студию.'
            : 'Не удалось отменить запись.',
        )
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myBookings(tenant.slug) })
    },
  })
}

/** Saves a booking made before signing in, proven by its private link. */
export function useClaimBooking() {
  const tenant = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (accessToken: string) => {
      const { error } = await getSupabaseClient().rpc('claim_booking', { access_token: accessToken })
      if (error !== null) {
        throw new Error(
          error.code === '42501'
            ? 'Эта запись уже сохранена в другом профиле.'
            : 'Не удалось сохранить запись в профиль.',
        )
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myBookings(tenant.slug) })
    },
  })
}
